import {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useEffect,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { buildPipelineUrl } from 'src/components/pipelines/utils/query-branches';
import { FetchRefOptionsPayload } from 'src/redux/create-branch/actions';
import {
  getPullRequestDestinationRepo,
  getPullRequestSourceRepo,
} from 'src/redux/pull-request/selectors';
import { BranchSelectorType } from 'src/redux/repository/branch/branch-redux';
import { Ref as BranchRef } from 'src/sections/create-branch';
import { LOADING_STATE } from 'src/sections/create-branch/constants';
import {
  getNewBranchEnabledRef,
  getRef,
  getRefs,
} from 'src/sections/create-branch/utils';
import {
  fetchRefOptions,
  generateRefUrl,
} from 'src/sections/repository/components/ref-selector/utils';
import { messages } from 'src/sections/repository/sections/create-pull-request/components/create-pull-request.i18n';
import { Ref } from 'src/sections/repository/types';
import {
  getCurrentRepositoryFullSlug,
  getRepositoryBranchingModel,
  getRepositoryMainBranch,
} from 'src/selectors/repository-selectors';
import { getCurrentUserUuid } from 'src/selectors/user-selectors';
import { authRequest } from 'src/utils/fetch';
import {
  captureMessageWithTags,
  captureExceptionWithTags,
} from 'src/utils/sentry';

import { buildRefUrls } from './utils';

/**
 * Builds the request URLs based on whether we're using pipeline ref names and if there's a filter
 */
const buildRequestUrls = (
  repoSlug: string,
  filter: string,
  excludeSourceBranchQuery: string,
  usePipelinesRefNames?: boolean,
  currentUserUuid?: string
): string[] => {
  if (usePipelinesRefNames && !filter) {
    return [
      buildPipelineUrl(repoSlug, {
        page: 1,
        currentUserUuid,
      }),
    ];
  }
  return buildRefUrls(repoSlug, 'branch', filter, excludeSourceBranchQuery);
};

type RefFetchActionOptions = {
  /** A source branch can be optionally passed in to hide it from the list of fetched refs.
   * E.g. The retarget dest branch selector should hide the source branch from listed refs
   *  so users cannot retarget to the same branch. */
  sourceBranch?: string;
  /** Function to run upon starting the ref fetching process */
  onStart?: () => void;
  /** Function to run upon successfully fetching refs */
  onSucceed?: () => void;
  /** Function to run upon encountering an error while fetching refs */
  onError?: (e: Error & { status?: number }) => void;
  /** Destination repo to fetch refs for. This is used to build the correct ref URL.
   * If omitted, the `repositoryFullSlug` will be used. */
  destinationRepoName?: string;
  /** Forked repos can provide their parent repo information to allow */
  parentRepo?: {
    name: string;
    mainBranch: Ref;
  };
  /** By default, the main branch of a repo will be displayed first.
   * Setting this to true will ignore this priority and list the main branch normally. */
  ignoreMainBranchPriority?: boolean;
  /** Name of the new branch that is created when copying a branch from the source repo
   * to the dest repo on a cross-repo PR. This will show up as 'newBranchName (New)' in the
   * branch selector. */
  newBranchName?: string;
  usePipelinesRefNamesFFEnabled?: boolean;
};

export const useRefFetchActions = (
  options?: RefFetchActionOptions
): {
  fetchRefs: (payload?: FetchRefOptionsPayload) => Promise<void>;
  fetchNextRefs: (payload?: FetchRefOptionsPayload) => Promise<void>;
  refetchMainDestRef: (
    destRepoFullSlug: string,
    setDest: Dispatch<SetStateAction<BranchRef>>
  ) => Promise<void>;
  state: BranchSelectorType;
} => {
  const repositoryFullSlug = useSelector(getCurrentRepositoryFullSlug);
  const branchingModel = useSelector(getRepositoryBranchingModel);
  const mainBranch = useSelector(getRepositoryMainBranch);
  const currentUserUuid = useSelector(getCurrentUserUuid);

  const destRepo = useSelector(getPullRequestDestinationRepo);
  const sourceRepo = useSelector(getPullRequestSourceRepo);

  const intl = useIntl();

  const sameRepo =
    sourceRepo && destRepo && sourceRepo.full_name === destRepo.full_name;

  const branchSelector: BranchSelectorType = {
    loadingState: LOADING_STATE.SUCCESS,
    refs: [],
    hasMoreRefs: false,
    next: undefined,
    usePipelinesRefNames: false,
  };

  const [state, setState] = useState(branchSelector);

  const {
    sourceBranch,
    onStart,
    onSucceed,
    onError,
    destinationRepoName,
    parentRepo,
    ignoreMainBranchPriority,
    newBranchName,
    usePipelinesRefNamesFFEnabled,
  } = options || {};

  const repoSlug = destinationRepoName ?? repositoryFullSlug;

  /**
   * Helper function that returns the main branch name
   * from the provided repo name.
   *
   * This could either be the main branch of the current
   * repo, or the parent of a forked repo's main branch.
   * @param {string} repoName - Name of the repo
   * @returns {Ref} - The main branch ref
   */
  const getMainBranchFromRepoName = useCallback(
    (repoName: string) => {
      if (repoName === parentRepo?.name) {
        return parentRepo?.mainBranch;
      }
      return mainBranch;
    },
    [mainBranch, parentRepo?.name, parentRepo?.mainBranch]
  );

  /**
   * Callback function to fetch refs for a repo.
   * This will update the refs and loading state that is passed by this hook.
   *
   * If a search filter is provided, we will fetch exact matches in one request, then similar
   * matches in a follow up request. The results of both requests will be joined and the state
   * will be updated.
   * @param {FetchRefOptionsPayload} payload - Optional payload that may contain a search filter
   * @returns {void}
   */
  const fetchRefs = useCallback(
    async (payload?: FetchRefOptionsPayload) => {
      if (!repoSlug) {
        return;
      }

      if (onStart) onStart();
      const filter = payload?.search || '';
      const usePipelinesRefNames = usePipelinesRefNamesFFEnabled && !filter;
      setState({
        loadingState: LOADING_STATE.LOADING,
        refs: [],
        hasMoreRefs: false,
        next: undefined,
        usePipelinesRefNames,
      });

      // If the source and destination repos are the same, the source branch
      // cannot be the destination branch, so passing in a Branch object
      // with source branch name in it will exclude it from the results.
      // This is only used when a sourceBranch is passed in to this hook.
      const excludeSourceBranchQuery =
        sourceBranch && sameRepo ? `name != "${sourceBranch}"` : '';

      const requestUrls = buildRequestUrls(
        repoSlug,
        filter,
        excludeSourceBranchQuery,
        usePipelinesRefNames,
        currentUserUuid
      );

      try {
        const fetchPromises = requestUrls.map(url =>
          fetchRefOptions(
            url,
            !ignoreMainBranchPriority && filter === '', // only order main branch first if filter is empty
            filter,
            'branch',
            getMainBranchFromRepoName(repoSlug),
            branchingModel,
            onError,
            usePipelinesRefNames
          ).then(response => ({ response, url }))
        );

        const responses = await Promise.all(fetchPromises);

        if (onSucceed) onSucceed();

        // Combine all fetched refs into one array
        const allPreparedRefs: Ref[] = responses.flatMap(
          r => r.response.preparedRefs
        );

        // Use the next url from the last request
        const next = responses.find(({ url }) =>
          url.includes(requestUrls[requestUrls.length - 1])
        )?.response.next;

        const newBranchRef = newBranchName
          ? [
              getNewBranchEnabledRef({
                name: intl.formatMessage(messages.newBranchName, {
                  branchName: newBranchName,
                }),
                type: 'branch',
                isNewBranch: true,
              }),
            ]
          : [];

        setState(s => ({
          ...s,
          refs: [...newBranchRef, ...getRefs(allPreparedRefs)],
          next,
          hasMoreRefs: !!next,
          loadingState: LOADING_STATE.SUCCESS,
        }));
      } catch {
        setState(prevState => ({
          ...prevState,
          loadingState: LOADING_STATE.ERROR,
        }));
      }
    },
    [
      repoSlug,
      onStart,
      sourceBranch,
      sameRepo,
      usePipelinesRefNamesFFEnabled,
      currentUserUuid,
      onSucceed,
      newBranchName,
      intl,
      ignoreMainBranchPriority,
      getMainBranchFromRepoName,
      branchingModel,
      onError,
    ]
  );

  useEffect(() => {
    if (usePipelinesRefNamesFFEnabled && repoSlug) {
      fetchRefs();
    }
  }, [usePipelinesRefNamesFFEnabled, repoSlug, fetchRefs]);

  /**
   * Callback function to fetch the next page of refs for a repo.
   * This function is used to fetch the next page of results from the request URL used
   * by `fetchRefs`.
   * @param {FetchRefOptionsPayload} payload - Optional payload that may contain a search filter
   * @returns {void}
   */
  const fetchNextRefs = useCallback(
    async (payload?: FetchRefOptionsPayload) => {
      const { next: nextUrl } = state;
      if (!nextUrl || !repoSlug) {
        return;
      }
      const search = payload?.search;

      try {
        const { preparedRefs, next } = await fetchRefOptions(
          nextUrl,
          false,
          search || '',
          'branch',
          getMainBranchFromRepoName(repoSlug),
          branchingModel,
          undefined,
          state.usePipelinesRefNames
        );

        setState(s => ({
          ...s,
          refs: [...s.refs, ...getRefs(preparedRefs)],
          next,
          hasMoreRefs: !!next,
          loadingState: LOADING_STATE.SUCCESS,
        }));
      } catch {
        setState(prevState => ({
          ...prevState,
          loadingState: LOADING_STATE.ERROR,
        }));
      }
    },
    [state, repoSlug, getMainBranchFromRepoName, branchingModel]
  );

  /**
   * Callback function to fetch the main ref if the destination repository changes.
   * This function is used for cross-repo PRs.
   * @param {string} destRepoFullSlug - Destination repo full slug
   * @param {Dispatch<SetStateAction<BranchRef>>} setDest - Action to set the destination branch
   * @returns {void}
   */
  const refetchMainDestRef = useCallback(
    async (
      destRepoFullSlug: string,
      setDest: Dispatch<SetStateAction<BranchRef>>
    ) => {
      const mainBranchFromRepo = getMainBranchFromRepoName(destRepoFullSlug);

      if (!mainBranchFromRepo) {
        return;
      }

      const url = generateRefUrl(
        destRepoFullSlug,
        mainBranchFromRepo.name,
        'branch',
        null, // don't filter out the main branch
        undefined, // don't filter out any of the branching model
        '-target.date',
        { type: 'exact', exact: true }
      );
      try {
        const response = await fetch(authRequest(url));
        if (!response.ok) {
          throw Error();
        }
        const { values } = await response.json();
        if (values.length === 0 || !values[0]) {
          captureMessageWithTags(
            'Failed to change branch view destination branch'
          );
        } else {
          const ref = getRef(values[0]);
          setDest(ref);
        }
      } catch (e) {
        captureExceptionWithTags(e);
      }
    },
    [getMainBranchFromRepoName]
  );

  return {
    fetchRefs,
    fetchNextRefs,
    refetchMainDestRef,
    state,
  };
};
